import Phaser from 'phaser';
import { biomeForLevel } from '../../content/biomes';
import { MOWER_DEFS } from '../../content/mowers';
import { NAME_NOUNS } from '../../content/names';
import { raceLawn, type RaceLawn, type RaceSize } from '../../gen/modes';
import { GreedyPlanner } from '../../gen/solver/greedyPlanner';
import { equippedPaint, equippedStripes } from '../../meta/economy';
import { postedRun } from '../../meta/online/outbox';
import { RACE_PAID_PER_DAY, racePay } from '../../meta/online/race';
import { postedLawn } from '../../meta/online/run';
import { haptics } from '../../platform/haptics';
import { TICK_RATE } from '../../sim/constants';
import type { SimEvent } from '../../sim/events';
import type { Intent, SteerIntent } from '../../sim/mower';
import { encodeIntent, hashSimState } from '../../sim/replay';
import { Simulation } from '../../sim/simulation';
import { gameAudio } from '../audio/audio';
import { exposeDebug } from '../debug';
import { InputController } from '../input/InputController';
import { HUD_HEIGHT } from '../layout';
import { currentRace, type RaceResult, type RaceSession, type RaceView } from '../live';
import { addFriend, isFriend, myFriends } from '../online';
import { RaceCoins } from '../raceCoins';
import { saves, todayKey } from '../records';
import { LevelView, MOWER_DEPTH } from '../render/LevelView';
import { LiveGhost } from '../render/LiveGhost';
import { loadControlScheme } from '../settings';
import { formatTime } from '../ui/widgets';
import type { HudScene } from './HudScene';
import type { ResultsModel } from './ResultsScene';
import { wipeTo } from './WipeScene';
import { nicknameOf, whoText } from '../nicknames';

/** Where a race starts: its lawn (seed and size) and its first tick on this phone's clock. */
export interface RaceLevelData {
  seed: string;
  size: RaceSize;
  atLocal: number;
}

const BACKGROUND = '#2b4a26';
/** Progress (with the state's hash) goes to the friend every this many ticks: four a second. */
const PROGRESS_EVERY = 15;
/** Ticks run in one frame at most (a slow frame catches up; see AWAY_GAP_MS for longer). */
const MAX_CATCH_UP = 900;
/**
 * A gap between frames longer than this is time away (the app in the background, the phone
 * locked): the mower waits where it was instead of driving on without the player (owner,
 * 2026-10-25). Results compare mowing ticks, so a wait helps nobody; 10 seconds away still forfeits.
 */
const AWAY_GAP_MS = 250;

type Phase = 'countdown' | 'racing' | 'finished' | 'result';

/**
 * A live race on twin lawns (docs/LIVE.md): this player's lawn and mower, the friend as a warm
 * see-through mower replaying their turns, and the shared coins. The race clock is real time from
 * the room's start, less any time the app was away (the mower waits; it never drives on alone),
 * and the friend is shown at this player's tick, so both racers' ticks compare. Turns go to the
 * room as they are made and progress four times a second; at the end the whole run goes for
 * checking and the room's result comes back.
 */
export class RaceLevelScene extends Phaser.Scene {
  private session!: RaceSession;
  private data_!: RaceLevelData;
  private race!: RaceLawn;
  private sim!: Simulation;
  private view!: LevelView;
  private ghost!: LiveGhost;
  private coins!: RaceCoins;
  /** The lawn's coins, by their index in the level's pickups. */
  private coinIndexes: number[] = [];
  private phase: Phase = 'countdown';
  private sent = 0;
  private lastProgress = -1;
  private shownCount = '';
  private queuedTurn: SteerIntent | null = null;
  private autopilot = false;
  private planner: GreedyPlanner | null = null;
  private settled = false;
  private noticed = { left: false, line: false, first: false, out: false, crashed: false };
  /** Time the app was away since the start (not raced: the mower waited). */
  private awayMs = 0;
  private lastFrame = 0;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    super('RaceLevel');
  }

  init(data: RaceLevelData): void {
    this.data_ = data;
    this.phase = 'countdown';
    this.sent = 0;
    this.lastProgress = -1;
    this.shownCount = '';
    this.queuedTurn = null;
    this.planner = null;
    this.settled = false;
    this.noticed = { left: false, line: false, first: false, out: false, crashed: false };
    this.awayMs = 0;
    this.lastFrame = 0;
    this.autopilot =
      import.meta.env.DEV && new URLSearchParams(window.location.search).has('autopilot');
  }

  create(): void {
    const session = currentRace();
    if (!session) {
      wipeTo(this, 'Versus');
      return;
    }
    this.session = session;
    // Fresh friends for the result's ADD FRIEND (a request may have been accepted since).
    void myFriends();
    this.race = raceLawn(this.data_.seed, this.data_.size);
    const level = this.race.def;
    const mower = MOWER_DEFS[this.race.loaner];
    this.layoutCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layoutCamera);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layoutCamera);
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.scene.stop('Hud');
      this.scene.stop('Results');
      gameAudio.stopMower();
      this.ghost?.destroy();
    });

    this.sim = new Simulation(level.sim, mower);
    const save = saves().save;
    this.view = new LevelView(this, this.sim, level.art, {
      reducedShake: save.settings.reducedShake,
      biome: biomeForLevel(level.level ?? 1).id,
      paint: equippedPaint(save, this.race.loaner),
      stripes: equippedStripes(save),
    });
    this.view.applyEvents(this.sim.drainEvents());
    this.ghost = new LiveGhost(this, level.sim, this.race.loaner, MOWER_DEPTH - 1);
    this.coinIndexes = (level.sim.pickups ?? []).flatMap((p, i) => (p.kind === 'coin' ? [i] : []));
    this.coins = new RaceCoins(this.coinIndexes.length);

    new InputController(
      this,
      loadControlScheme(save.settings.controls),
      { onActivity: () => false, onIntent: this.onIntent },
      (y) => (y < HUD_HEIGHT ? 'ui' : null),
    );
    const keyboard = this.input.keyboard;
    keyboard?.on('keydown-ESC', () => this.confirmLeave());
    if (import.meta.env.DEV) keyboard?.on('keydown-P', () => (this.autopilot = !this.autopilot));

    this.scene.launch('Hud', { onMenu: () => this.confirmLeave() });
    this.scene.bringToTop('Hud');
    // The HUD keeps a message across restarts (for the campaign's prompts): a rematch's "Waiting
    // for ..." must not cover the new race.
    this.hud()?.hideMessage();
    this.unsubscribe = session.subscribe(this.onView);
    this.onView(session.view);
    exposeDebug({ scene: this, sim: this.sim });
  }

  override update(): void {
    const now = Date.now();
    // Time away after the start (a long gap between frames) is not raced: the mower waited.
    const start = this.data_.atLocal;
    if (this.lastFrame && now - this.lastFrame > AWAY_GAP_MS && now > start) {
      this.awayMs += now - Math.max(this.lastFrame, start);
    }
    this.lastFrame = now;
    const elapsed = now - start - this.awayMs;
    if (this.phase === 'countdown') {
      if (elapsed < 0) {
        const count = String(Math.ceil(-elapsed / 1000));
        if (count !== this.shownCount) {
          this.shownCount = count;
          this.hud()?.showCountdown(count);
          gameAudio.play('click', 1 + (3 - Number(count)) * 0.12);
        }
      } else {
        this.go();
      }
    }
    const target = Math.max(0, Math.floor((elapsed * TICK_RATE) / 1000));
    if (this.phase === 'racing') {
      this.sendTurns();
      for (let i = 0; i < MAX_CATCH_UP && this.sim.tick < target; i++) {
        if (this.sim.status !== 'running') break;
        if (this.autopilot) this.steerAutopilot();
        this.handleEvents(this.sim.step());
        this.sendTurns();
        if (this.sim.tick - this.lastProgress >= PROGRESS_EVERY) this.sendProgress();
      }
      gameAudio.updateMower(this.sim);
      if (this.sim.status !== 'running') this.finishMine();
    }
    if (this.phase !== 'countdown') {
      this.ghost.advance(
        this.phase === 'racing' ? this.sim.tick : target,
        this.session.friend,
        this.onFriendCoin,
      );
      // The friend cleared the lawn sooner than this player can: the race is theirs.
      const theirs = this.ghost.sim;
      if (this.phase === 'racing' && theirs.status === 'won' && theirs.tick <= this.sim.tick) {
        if (!this.noticed.first) {
          this.noticed.first = true;
          this.hud()?.showNotice(`${this.friendShort()} FINISHED FIRST`);
        }
        this.finishMine();
      }
      // The friend's mower broke down (or ran dry): this player wins without mowing the rest.
      const crashed = theirs.status !== 'won' && theirs.status !== 'running';
      if (this.phase === 'racing' && crashed && theirs.tick <= this.sim.tick) {
        if (!this.noticed.crashed) {
          this.noticed.crashed = true;
          this.hud()?.showNotice(`${this.friendShort()} BROKE DOWN: YOU WIN`);
        }
        this.finishMine();
      }
    }
    const alpha = this.phase === 'racing' ? ((elapsed * TICK_RATE) / 1000) % 1 : 0;
    this.view.render(alpha, this.phase === 'racing');
    this.ghost.render(alpha);
    this.updateHud();
  }

  // ── The race ───────────────────────────────────────────────────────────────────────────────

  private go(): void {
    this.phase = 'racing';
    this.hud()?.showCountdown('GO!');
    gameAudio.play('click', 1.5);
    this.time.delayedCall(350, () => this.hud()?.hideCountdown());
    gameAudio.startMower(this.sim, this.race.loaner);
    if (this.queuedTurn) this.sim.enqueue(this.queuedTurn);
    this.queuedTurn = null;
    this.view.previewTurn(null);
  }

  private readonly onIntent = (intent: Intent): void => {
    if (this.phase === 'countdown' && (intent.type === 'face' || intent.type === 'turn')) {
      this.queuedTurn = intent;
      this.view.previewTurn(intent);
      return;
    }
    if (this.phase === 'racing' && !this.autopilot) this.sim.enqueue(intent);
  };

  /** Each new turn to the friend, as soon as it is made. */
  private sendTurns(): void {
    const log = this.sim.intentLog;
    for (; this.sent < log.length; this.sent++) {
      const { tick, intent } = log[this.sent]!;
      this.session.sendInput(tick, encodeIntent(intent));
    }
  }

  private sendProgress(): void {
    this.lastProgress = this.sim.tick;
    this.session.sendProgress(this.sim.tick, this.sim.grid.percentMowed, hashSimState(this.sim));
  }

  /** This player's race is over (cleared, out, or beaten): the run goes to the room. */
  private finishMine(): void {
    if (this.phase !== 'racing') return;
    this.phase = 'finished';
    this.sendTurns();
    this.sendProgress();
    gameAudio.stopMower();
    const run = postedRun(
      { kind: 'race', seed: this.data_.seed, size: this.data_.size },
      this.race.loaner,
      [postedLawn(this.sim)],
      { score: 0, status: this.sim.status },
    );
    this.session.finish(run);
    if (this.sim.status === 'won') haptics.heavy();
    if (!this.session.view.result) this.hud()?.showMessage(`Waiting for ${this.friendName()}...`);
  }

  private handleEvents(all: readonly SimEvent[]): void {
    // A coin the friend reached first is not this player's to pick up: no sparkle, no sound.
    const events = all.filter((e) => {
      if (e.type !== 'pickup' || e.kind !== 'coin') return true;
      const coin = this.coinIndexes.indexOf(e.index);
      return coin < 0 || this.coins.mineAt(coin, this.sim.tick);
    });
    this.view.applyEvents(events);
    gameAudio.onEvents(events, this.sim);
    for (const e of events) {
      if (e.type === 'turned' || e.type === 'trampled') haptics.tap();
      else if (e.type === 'smashed' || e.type === 'chewed' || e.type === 'crashed')
        haptics.impact();
      else if (e.type === 'mowerBroken') haptics.heavy();
      else if (e.type === 'treeFell') this.planner = null;
    }
  }

  private readonly onFriendCoin = (coin: number, tick: number): void => {
    const what = this.coins.friendAt(coin, tick);
    if (what === 'taken') this.view.friendTook(this.coinIndexes[coin]!);
    else if (what === 'takenBack') this.hud()?.showNotice(`${this.friendShort()} GOT THERE FIRST`);
    else if (what === 'tied') this.hud()?.showNotice('A TIE: NOBODY GETS IT');
  };

  private steerAutopilot(): void {
    if (this.sim.mower.progress !== 0) return;
    this.planner ??= new GreedyPlanner(this.sim);
    const action = this.planner.nextAction();
    if (action && action !== 'straight') this.sim.enqueue({ type: 'turn', turn: action });
  }

  // ── The room ───────────────────────────────────────────────────────────────────────────────

  private readonly onView = (view: RaceView): void => {
    if (!this.scene.isActive()) return;
    // A rematch: the new lawn's countdown.
    if (view.start && view.start.seed !== this.data_.seed) {
      this.scene.restart({
        seed: view.start.seed,
        size: view.size ?? this.data_.size,
        atLocal: view.start.atLocal,
      } satisfies RaceLevelData);
      return;
    }
    if (view.result && !this.settled) {
      this.showResult(view.result);
      return;
    }
    // Counted out (away past the grace): this race is over for this player, said plainly.
    const me = view.players.find((p) => p.seat === view.you);
    if (me?.out && this.phase === 'racing') {
      this.phase = 'finished';
      gameAudio.stopMower();
      this.hud()?.showMessage(
        `You were away too long: out of this race. Waiting for ${this.friendName()}...`,
      );
    }
    const friendOut = view.players.find((p) => p.seat !== view.you)?.out === true;
    if ((view.friendLeft || friendOut) && !this.noticed.left && this.phase !== 'result') {
      this.noticed.left = true;
      this.hud()?.showNotice(`${this.friendShort()} DROPPED OUT: FINISH TO WIN`);
    }
    if (view.line === 'reconnecting' && !this.noticed.line) {
      this.noticed.line = true;
      this.hud()?.showNotice('RECONNECTING...');
    }
    if (view.line === 'open') this.noticed.line = false;
    if (view.line === 'closed' && !view.result && this.phase !== 'result') {
      this.phase = 'result';
      gameAudio.stopMower();
      this.showCard({
        title: 'Race over',
        info: [view.closedWhy ?? 'The connection to the race was lost.'],
        coinLines: [],
        buttons: [{ label: 'Done', action: () => this.done(), primary: true }],
      });
    }
  };

  /** The room's verdict: who won, the times, the coins, and what this player is paid. */
  private showResult(result: RaceResult): void {
    this.settled = true;
    this.phase = 'result';
    gameAudio.stopMower();
    this.hud()?.hideMessage();
    // (A race on twin lawns is always two: seat 0 or 1.)
    const you = (this.session.view.you ?? 0) as 0 | 1;
    const friend = (1 - you) as 0 | 1;
    const name = this.friendName();
    const today = todayKey();
    const save = saves().save;
    const paidToday = save.race.day === today ? save.race.paid : 0;
    const pay = racePay(this.data_.size, you, result.winner, result.coins, paidToday);
    saves().update((s) => {
      if (s.race.day !== today) s.race = { ...s.race, day: today, paid: 0 };
      s.race.paid++;
      s.race.races++;
      if (result.winner === you) s.race.wins++;
      s.coins += pay.bonus + pay.coins;
    });
    // A stopped run that broke down (seen here: this phone's own mower, and the friend's replay).
    const brokeDown = (seat: 0 | 1) => {
      const status = seat === you ? this.sim.status : this.ghost.sim.status;
      return status !== 'won' && status !== 'running';
    };
    const line = (seat: 0 | 1, who: string) => {
      const end = result.ends[seat];
      const ticks = result.ticks[seat];
      const how =
        end === 'cleared' && ticks !== null
          ? formatTime(ticks, TICK_RATE)
          : end === 'stopped'
            ? brokeDown(seat)
              ? 'broke down'
              : 'did not finish'
            : end === 'forfeit'
              ? 'dropped out'
              : 'not counted';
      return `${who}: ${how}`;
    };
    const held = (seat: 0 | 1) => result.coins.filter((c) => c === seat).length;
    const title =
      result.winner === you ? 'You won!' : result.winner === null ? 'A draw!' : `${name} won`;
    const rematch = !this.session.view.friendLeft;
    // Racing someone not yet a friend: ADD FRIEND (docs/ONLINE.md, "Boards").
    const other = this.session.view.players.find((p) => p.seat !== you)?.player;
    const befriend = other && !isFriend(other) && other !== saves().save.online.player?.id;
    this.resultCard = {
      title,
      ...(result.winner === you ? { cheer: true } : {}),
      info: [line(you, 'You'), line(friend, name), `Coins: ${held(you)} to ${held(friend)}`],
      coinLines: [
        ...(pay.bonus > 0
          ? [{ label: result.winner === null ? 'Draw bonus' : 'Win bonus', coins: pay.bonus }]
          : []),
        ...(pay.coins > 0 ? [{ label: 'Coins', coins: pay.coins }] : []),
      ],
      wallet: saves().save.coins,
      ...(paidToday >= RACE_PAID_PER_DAY ? { note: "Today's paid races are done." } : {}),
      buttons: [
        { label: 'Done', action: () => this.done() },
        ...(befriend ? [{ label: 'Add friend', action: () => void this.befriend(other) }] : []),
        ...(rematch ? [{ label: 'Rematch', action: () => this.askRematch(), primary: true }] : []),
      ],
    };
    this.showCard(this.resultCard);
  }

  /** The result card as shown (shown again, without ADD FRIEND, once they are friends). */
  private resultCard: ResultsModel | null = null;

  /** ADD FRIEND on the result: friends both ways, and the card says so. */
  private async befriend(id: string): Promise<void> {
    // A request they accept (docs/FRIENDS.md, step 3), unless they asked first.
    const result = await addFriend({ player: id });
    const card = this.resultCard;
    if (!card || this.phase !== 'result') return;
    this.resultCard = {
      ...card,
      note:
        typeof result === 'string'
          ? result
          : result.pending
            ? 'Friend request sent.'
            : `Friends with ${whoText(result.info.player, result.info.name)}!`,
      buttons: card.buttons.filter((b) => b.label !== 'Add friend'),
    };
    this.showCard(this.resultCard);
  }

  private askRematch(): void {
    this.session.rematch();
    this.scene.stop('Results');
    this.hud()?.showMessage(`Waiting for ${this.friendName()}...`);
  }

  /**
   * The pause button (or Esc): leaving is always asked first (owner, 2026-10-25). Mid-race it
   * forfeits; once this player's run is in it misses the result; after asking for a rematch it
   * gives up waiting. The race runs on meanwhile (a live race cannot pause). With the result card
   * up, its DONE is the way out.
   */
  /** Android's back button: DONE on the result card, otherwise asked first, as the pause button. */
  onBack(): void {
    if (this.phase === 'result' && this.scene.isActive('Results')) this.done();
    else this.confirmLeave();
  }

  private confirmLeave(): void {
    if (this.phase === 'result' && this.scene.isActive('Results')) return;
    const name = this.friendName();
    const [info, stay] =
      this.phase === 'result'
        ? [`Stop waiting for ${name}?`, 'Keep waiting']
        : this.phase === 'finished'
          ? ['Your run is in. Leave now and you miss the result.', 'Stay']
          : [`You forfeit, and ${name} wins.`, 'Keep racing'];
    this.showCard({
      title: 'Leave the race?',
      info: [info],
      coinLines: [],
      buttons: [
        { label: 'Leave', action: () => this.done() },
        { label: stay, action: () => this.scene.stop('Results'), primary: true },
      ],
    });
  }

  /** Out of the race for good: back to CLUB > VERSUS. */
  private done(): void {
    this.session.leave();
    wipeTo(this, 'Versus');
  }

  private showCard(model: ResultsModel): void {
    this.scene.stop('Results');
    this.scene.launch('Results', model);
    this.scene.bringToTop('Results');
  }

  private friendName(): string {
    const view = this.session.view;
    const friend = view.players.find((p) => p.seat !== view.you);
    return friend ? whoText(friend.player, friend.name) : 'Your friend';
  }

  /** The friend's name for a notice across the lawn: its last word ("HAWK"), so it fits. */
  private friendShort(): string {
    const view = this.session.view;
    const friend = view.players.find((p) => p.seat !== view.you);
    // A nickname fits (at most 10 characters); a name gives its last word.
    const nickname = nicknameOf(friend?.player);
    if (nickname) return nickname;
    const name = friend?.name;
    const noun = name?.kind === 'words' ? NAME_NOUNS[name.parts[1]] : undefined;
    return (noun ?? 'FRIEND').toUpperCase();
  }

  private updateHud(): void {
    this.hud()?.setInfo({
      label: `VS ${this.session.friend.pct}%`,
      timer: 'plain',
      percent: this.sim.grid.percentMowed,
      hp: this.sim.mower.hp,
      maxHp: this.sim.mowerSpec.maxHp,
      wallet: saves().save.coins,
      pending: this.phase === 'result' ? null : this.coins.held,
      fuel:
        this.sim.mowerSpec.fuelTiles === undefined
          ? null
          : this.sim.mower.fuel / this.sim.mowerSpec.fuelTiles,
      slow: this.sim.mowerSpec.throttle ? this.sim.mower.slow : null,
      tools: [],
      autopilot: this.autopilot,
      flat: this.sim.mower.flat,
    });
  }

  private hud(): HudScene | null {
    return this.scene.get('Hud') as HudScene | null;
  }

  private readonly layoutCamera = (): void => {
    const { width, height } = this.scale.gameSize;
    this.cameras.main.setViewport(0, HUD_HEIGHT, width, height - HUD_HEIGHT);
    this.cameras.main.setBackgroundColor(BACKGROUND);
  };
}
